import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import {
  HiOutlineFolder,
  HiOutlineUsers,
  HiOutlineDocument,
  HiOutlineClock,
  HiOutlineCalendarDays,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineInformationCircle,
} from 'react-icons/hi2'
import {
  Banner,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Spinner,
} from '../../components/ui'

export default function EmployeeDashboard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { ownerAccountName } = useAuth()
  const { getEntity } = useWorkspace()
  const [recentSessions, setRecentSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const response = await apiService.get('/dashboard/summary/')
        if (cancelled) return
        if (response.success && response.data.data.recent_sessions) {
          setRecentSessions(response.data.data.recent_sessions)
          setError(null)
        } else {
          setError(response.error?.message || 'Failed to load recent sessions')
        }
      } catch {
        if (!cancelled) setError('Failed to load recent sessions')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="h-6 w-1/2 bg-muted rounded animate-pulse" />
          <div className="h-4 w-1/3 bg-muted rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="flex justify-center pt-4"><Spinner /></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Banner tone="danger" title="Couldn't load recent sessions">{error}</Banner>
        <Button onClick={() => window.location.reload()}>{t('common.retry')}</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t('dashboard.employee.eyebrow', 'Switched workspace')}
        title={t('dashboard.employee.title', "{{name}}'s recent {{entity}}", {
          name: ownerAccountName,
          entity: getEntity('session').plural.toLowerCase(),
        })}
        subtitle={t('dashboard.employee.subtitle', "View recent activity in {{name}}'s account", {
          name: ownerAccountName,
        })}
      />

      <Card padding="none">
        <Card.Header className="px-5 pt-5 pb-3">
          <div className="flex items-center justify-between gap-3 w-full">
            <div className="flex items-center gap-2">
              <HiOutlineClock className="w-5 h-5 text-ink-muted" />
              <h3 className="text-base font-semibold text-ink-primary">
                {t('dashboard.employee.recentTitle', 'Recent {{entity}}', { entity: getEntity('session').plural })}
              </h3>
            </div>
            <span className="text-xs text-ink-muted">
              {t('dashboard.employee.last10', 'Last 10 {{entity}}', { entity: getEntity('session').plural.toLowerCase() })}
            </span>
          </div>
        </Card.Header>

        <div className="px-5 pb-5">
          {recentSessions.length === 0 ? (
            <EmptyState
              icon={HiOutlineCalendarDays}
              title={t('dashboard.employee.emptyTitle', 'No recent {{entity}} found', { entity: getEntity('session').plural.toLowerCase() })}
              description={t('dashboard.employee.emptyDescription', '{{entity}} will appear here once they are created', { entity: getEntity('session').plural })}
              compact
            />
          ) : (
            <ul className="space-y-2">
              {recentSessions.map((session) => (
                <li
                  key={session.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/40 hover:bg-muted transition-colors cursor-pointer"
                  onClick={() => navigate(`/sessions/${session.id}`)}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 bg-brand-soft text-brand rounded-lg flex items-center justify-center flex-shrink-0">
                      <HiOutlineClock className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-sm font-semibold text-ink-primary truncate">{session.title}</h4>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-ink-muted">
                        <span className="inline-flex items-center gap-1">
                          <HiOutlineFolder className="w-3.5 h-3.5" />
                          {session.case_title}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <HiOutlineUsers className="w-3.5 h-3.5" />
                          {session.client_name}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <HiOutlineDocument className="w-3.5 h-3.5" />
                          {session.document_count}{' '}
                          {session.document_count !== 1
                            ? getEntity('document').plural.toLowerCase()
                            : getEntity('document').singular.toLowerCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-end flex-shrink-0">
                    <p className="text-xs font-semibold text-ink-primary whitespace-nowrap">
                      {new Date(session.date).toLocaleDateString()}
                    </p>
                    {session.ttime && (
                      <p className="text-[11px] text-ink-muted mt-0.5">{session.ttime}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      <Banner
        tone="warning"
        icon={HiOutlineInformationCircle}
        title={t('dashboard.employee.limitedTitle', 'Limited dashboard view')}
      >
        <p>
          {t('dashboard.employee.limitedBody', "As a member of {{name}}'s workspace, you see recent {{sessions}} but not full account statistics. Access {{cases}}, {{clients}}, and {{documents}} via the side navigation based on your role.", {
            name: ownerAccountName,
            sessions: getEntity('session').plural.toLowerCase(),
            cases: getEntity('case').plural.toLowerCase(),
            clients: getEntity('client').plural.toLowerCase(),
            documents: getEntity('document').plural.toLowerCase(),
          })}
        </p>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          <button
            onClick={() => navigate('/cases')}
            className="text-sm font-medium hover:underline inline-flex items-center gap-1"
          >
            {t('dashboard.employee.viewCases', 'View {{entity}}', { entity: getEntity('case').plural })}
            <HiOutlineArrowTopRightOnSquare className="w-3 h-3 rtl:rotate-[-90deg]" />
          </button>
          <button
            onClick={() => navigate('/documents')}
            className="text-sm font-medium hover:underline inline-flex items-center gap-1"
          >
            {t('dashboard.employee.browseDocuments', 'Browse {{entity}}', { entity: getEntity('document').plural })}
            <HiOutlineArrowTopRightOnSquare className="w-3 h-3 rtl:rotate-[-90deg]" />
          </button>
        </div>
      </Banner>
    </div>
  )
}
